import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { User, Image, Calendar, DollarSign } from 'lucide-react';
import ArtworkCard from '../components/ArtworkCard';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

export default function ArtistProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('works');

  const isOwnProfile = user?.id === id;

  const fetchArtist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artists/${id}`);
      setArtist(response.data);
    } catch (error) {
      console.error('Error fetching artist:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSales = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/artists/${id}/sales`);
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchArtist();
    if (isOwnProfile) {
      fetchSales();
    }
  }, [fetchArtist, fetchSales, isOwnProfile]);

  if (loading) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center mb-12">
            <div className="w-32 h-32 rounded-full skeleton" />
            <div className="h-8 w-48 skeleton mt-6" />
            <div className="h-4 w-64 skeleton mt-4" />
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[#71717A]">Artist not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Profile Header */}
        <div className="flex flex-col items-center text-center mb-12">
          <div className="w-32 h-32 rounded-full bg-[#1A1A1A] flex items-center justify-center border-2 border-[#D4AF37]">
            {artist.profile_image ? (
              <img 
                src={artist.profile_image} 
                alt={artist.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <User size={48} className="text-[#71717A]" />
            )}
          </div>
          
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F5F5F0] mt-6">
            {artist.name}
          </h1>
          
          {artist.bio && (
            <p className="mt-4 text-[#A1A1AA] max-w-lg">
              {artist.bio}
            </p>
          )}
          
          <div className="mt-6 flex items-center gap-6 text-[#71717A]">
            <span className="flex items-center gap-2">
              <Image size={16} />
              {artist.artwork_count} works
            </span>
            <span className="flex items-center gap-2">
              <Calendar size={16} />
              Joined {new Date(artist.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Tabs */}
        {isOwnProfile && (
          <div className="tab-list">
            <button 
              className={`tab-item ${activeTab === 'works' ? 'active' : ''}`}
              onClick={() => setActiveTab('works')}
            >
              My Works
            </button>
            <button 
              className={`tab-item ${activeTab === 'sales' ? 'active' : ''}`}
              onClick={() => setActiveTab('sales')}
            >
              Sales & Earnings
            </button>
          </div>
        )}

        {/* Content */}
        {activeTab === 'works' && (
          <>
            {artist.artworks?.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-[#71717A]">No artworks yet</p>
              </div>
            ) : (
              <div className="bento-grid" data-testid="artist-artworks">
                {artist.artworks?.map((artwork, index) => (
                  <ArtworkCard key={artwork.id} artwork={artwork} featured={index === 0} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'sales' && isOwnProfile && sales && (
          <div>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              <div className="stat-card">
                <div className="stat-value">${sales.total_sales?.toFixed(2) || '0.00'}</div>
                <div className="stat-label">Total Sales</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">${sales.total_payout?.toFixed(2) || '0.00'}</div>
                <div className="stat-label">Your Earnings (82%)</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{sales.sales?.length || 0}</div>
                <div className="stat-label">Artworks Sold</div>
              </div>
            </div>

            {/* Sales Table */}
            {sales.sales?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Artwork</th>
                      <th>Sale Price</th>
                      <th>Commission (18%)</th>
                      <th>Your Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.sales.map(sale => (
                      <tr key={sale.id}>
                        <td>{new Date(sale.created_at).toLocaleDateString()}</td>
                        <td>{sale.artwork_id}</td>
                        <td className="text-[#D4AF37]">${sale.price?.toFixed(2)}</td>
                        <td className="text-[#71717A]">${sale.commission?.toFixed(2)}</td>
                        <td className="text-[#10B981]">${sale.artist_payout?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-[#71717A]">No sales yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
